import React, { useEffect, useState, useRef } from "react";
import {
  Input,
  PrimaryButton,
  SecondaryButton,
  Radio,
  INPUT_SIZES,
  Card,
  TextArea,
  RadioGroup,
  withTooltip,
} from "cdk-radial";
import withNavigationToBundles from "./withNavigationToBundles";
import { VIEW_BUNDLES, PRICING_LIST_PLANS, PRICING_ADD_PLAN, CREATE_A_CONTAINER } from "./screen";
import PricingBreadcrumbs from "./PricingBreadcrumbs";
import {
  SUB_PLAN_CHANGE
} from "../Utils/constants";
import types from "./types";
import EndpointContainer from "./EndpointContainer";

const options = [
  {
    id: "radio-without-bucket",
    label: "All Endpoints, same price",
    value: "nobucket",
  },
  {
    id: "radio-with-bucket",
    label: "Group Endpoints, into containers",
    value: "bucket",
  },
];

const AddAPlan = (props) => {
  const {
    auth,
    setScreen,
    setSubPlanDetails,
    getBundleApiEndpoints,
    createSubPlanContainer,
    setSubPlanContainer,
    setSelectedSubPlanContainer,
    clearSubPlanContainer,
    removeSubPlanContainer,
    setDraftSubPlanState,
    clearDraftSubPlanState,
    resetBundlePricingPlans,
    resetSelectedPricingPlan,
    resetBundlePlan,
    ApiBundleManagementReducer,
  } = props;

  const {
    selectedSubPlan = null,
    bundlePlan = {},
    selectedBundle = null,
    fetchingEndpointsAndChannels,
    errorFetchingEndpointsAndChannels,
    errorMessageFetchingEndpointsAndChannels = null,
    bundleApiEndpoints = {},
    bundleApiChannels = {},
    subPlanContainers = {},
    draftSubPlanState = null,
  } = ApiBundleManagementReducer;

  const plan = bundlePlan[selectedSubPlan] || {};
  const {
    radioValue = "nobucket",
  } = plan;

  // new local state
  const [title, setTitle] = useState(draftSubPlanState?.title ?? (plan.title || ""));
  const [containerName, setContainerName] = useState("All Endpoints");
  const [baseValue, setBaseValue] = useState(draftSubPlanState?.baseValue ?? (plan.baseValue || ""));
  const [description, setDescription] = useState(draftSubPlanState?.description ?? (plan.description || ""));
  // const [radioValue, setRadioValue] = useState(plan.radioValue || "nobucket");

  const container = radioValue === "nobucket" ? plan.containers[0] : [];

  // if radio value is bucket
  const containers = plan.containers || [];

  const [quotaLimit, setQuotaLimit] = useState(container?.quotaLimit || "");
  const [overages, setOverages] = useState(container?.overages || "");


  const [isTitleValid, setIsTitleValid] = useState(true);
  const [isBaseValueValid, setIsBaseValueValid] = useState(true);
  const [isDescriptionValid, setIsDescriptionValid] = useState(true);
  const [isQuotaLimitValid, setIsQuotaLimitValid] = useState(true);
  const [isOveragesValid, setIsOveragesValid] = useState(true);

  // Ref to hold latest form values (avoids stale closures in callbacks)
  const formValuesRef = useRef({ title, baseValue, description });
  formValuesRef.current = { title, baseValue, description };

  const containersCreatedByUser = Object.values(subPlanContainers).filter(container => container.name !== "All Endpoints");

  const isCommonFieldsMissing = title === "" || baseValue === "" || isNaN(Number(baseValue)) || Number(baseValue) < 0 || description === "";
  const isNoBucketFieldsMissing = radioValue === "nobucket" && (quotaLimit === "" || isNaN(Number(quotaLimit)) || Number(quotaLimit) < 0 || overages === "" || isNaN(Number(overages)) || Number(overages) < 0);
  
  const isRequiredFieldsMissing = isCommonFieldsMissing || isNoBucketFieldsMissing;
  const isBucketMissingContainers = radioValue === "bucket" && containersCreatedByUser.length === 0;

  const isSaveDisabled = isRequiredFieldsMissing || isBucketMissingContainers;

  let tooltipText = "";
  if (isRequiredFieldsMissing) {
    tooltipText = "Enter all the required fields";
  } else if (isBucketMissingContainers) {
    tooltipText = "Either select All Endpoints or create at least one container";
  }

  const breadcrumbs = [
    {
      label: "Bundle Management",
      onClick: () => {
        setScreen(VIEW_BUNDLES);
        clearSubPlanContainer();
        clearDraftSubPlanState();
        resetBundlePricingPlans();
        resetSelectedPricingPlan();
        resetBundlePlan();
      },
    },
    {
      label: "Bundle Pricing",
      onClick: () => {
        setScreen(PRICING_LIST_PLANS);
        clearSubPlanContainer();
        clearDraftSubPlanState();
        resetSelectedPricingPlan();
        resetBundlePlan();
      },
    },
    {
      label: "Add a Plan",
      onClick: () => {
        setScreen(PRICING_ADD_PLAN);
        clearSubPlanContainer();
        clearDraftSubPlanState();
      },
    },
    { label: `${title} Plan` },
  ];

  useEffect(() => {
    // Only fetch if we haven't already loaded endpoints for this bundle
    if (ApiBundleManagementReducer.endpointsBundleId === selectedBundle?.id) {
      return;
    }
    
    getBundleApiEndpoints({
      accessToken: auth.accessToken,
      bundleId: selectedBundle?.id,
    });
  }, [selectedBundle?.id, ApiBundleManagementReducer.endpointsBundleId, auth.accessToken, getBundleApiEndpoints]);



  // add default container values
  useEffect(() => {
    if (radioValue === "nobucket" && !fetchingEndpointsAndChannels) {
      setSubPlanContainer({
        id: container?.id,
        name: "All Endpoints",
        description: "Default Description",
        quotaLimit: quotaLimit,
        overages: overages,
        apiEndpoints: bundleApiEndpoints,
        channels: bundleApiChannels,
      })
    }
  }, [fetchingEndpointsAndChannels]);

  const onChangeTitle = (event) => {
    if (event.target.value.trim() === "") {
      setIsTitleValid(false);
    } else {
      setIsTitleValid(true);
    }
    setTitle(event.target.value);
  }

  const onChangeBaseValue = (event) => {
    const value = event.target.value;
    
    if (value.includes(".") && value.split(".")[1].length > 2) {
      return;
    }

    if (value === "" || isNaN(Number(value)) || Number(value) < 0) {
      setIsBaseValueValid(false);
    } else {
      setIsBaseValueValid(true);
    }
    setBaseValue(value);
  }

  const onChangeDescription = (event) => {
    if (event.target.value.trim() === "") {
      setIsDescriptionValid(false);
    } else {
      setIsDescriptionValid(true);
    }
    setDescription(event.target.value);
  }

  const onChangeRadio = (event) => {
    // reset quota limit and overages values on radio change
    // setRadioValue(event.target.value);
    //setSubPlanDetails({ field: SUB_PLAN_CHANGE.RADIO, value: event.target.value });
    if (event.target.value === "nobucket") {
      setSubPlanContainer({
        id: container?.id,
        name: "All Endpoints",
        description: "Default Description",
        quotaLimit: quotaLimit,
        overages: overages,
        apiEndpoints: bundleApiEndpoints,
        channels: bundleApiChannels,
      });
    } else {
      removeSubPlanContainer("All Endpoints");
    }
    setSubPlanDetails({ field: SUB_PLAN_CHANGE.RADIO, value: event.target.value });
  };

  const onChangeQuotaLimit = (event) => {
    const value = event.target.value;
    if (value === "" || isNaN(Number(value)) || parseInt(value) < 0) {
      setIsQuotaLimitValid(false);
    } else {
      setIsQuotaLimitValid(true);
    }
    setQuotaLimit(value);
    // let container = {
    //   name: "All Endpoints",
    //   description: "Default Description", // no mention within figma
    //   quotaLimit: event.target.value, // configure as per the paylaod
    //   overages: overages, // configure as per the paylaod
    //   apiEndpoints: bundleApiEndpoints, // configure as per the paylaod
    //   channels: bundleApiChannels, // configure as per the paylaod
    //  }
    // setSubPlanContainer(container);

  }

  const onChangeOverages = (event) => {
    const value = event.target.value;

    if (value.includes(".") && value.split(".")[1].length > 2) {
      return;
    }

    // set value within state
    if (value === "" || isNaN(Number(value)) || Number(value) < 0) {
      setIsOveragesValid(false);
    } else {
      setIsOveragesValid(true);
    }
    setOverages(value);
  }

  const onCreateEndpointContainer = () => {
    // Save draft state before navigating to create endpoint container screen
    setDraftSubPlanState({ title, baseValue, description });
    setScreen(CREATE_A_CONTAINER);
  }

  const onSavePlan = () => {
    if (selectedSubPlan === "custom") {
      setSubPlanDetails({ field: SUB_PLAN_CHANGE.TITLE, value: title });
    }
    setSubPlanDetails({ field: SUB_PLAN_CHANGE.BASE_VALUE, value: baseValue !== "" ? Number(baseValue) : "" });
    setSubPlanDetails({ field: SUB_PLAN_CHANGE.DESCRIPTION, value: description });
    if (radioValue === "nobucket") {
      createSubPlanContainer([
        {
          id: container?.id,
          name: "All Endpoints",
          description: "Default Description",
          quotaLimit: quotaLimit !== "" ? Number(quotaLimit) : "",
          overages: overages !== "" ? Number(overages) : "",
          endpoints: Object.values(bundleApiEndpoints || {}),
          channels: Object.values(bundleApiChannels || {}),
        },
      ]);
    } else {
      // filter All Endpoints from subPlanContainers
      // already selected endpoints should be marked
      // already selected channels should be marked
      createSubPlanContainer(containersCreatedByUser);
    }

    // Clear subPlanContainers to ensure clean state
    clearSubPlanContainer();
    clearDraftSubPlanState();

    // make api call to save the plan details
    setScreen(PRICING_ADD_PLAN);
    // clear any states if required
    // set all endpoints container
  }

  const onCancelPlan = () => {
    // clear subPlanContainer state
    clearSubPlanContainer();
    clearDraftSubPlanState();
    // navigate back to pricing list plans screen
    setScreen(PRICING_ADD_PLAN);
    // clear sub plan details from state
    // setSubPlanDetails({ field: SUB_PLAN_CHANGE.RESET });
  }

  const onRemoveBucket = (container) => {
    // make api call to remove the container
    // on success remove the container from state
    // for now removing from state directly
    removeSubPlanContainer(container.name);
  }

  const SaveButtonRenderer = (props) => (
    <span {...props} style={{ display: "inline-block", cursor: isSaveDisabled ? "not-allowed" : "pointer" }}>
      <PrimaryButton
        dataTestId="datatest-save-plan"
        iconColor="primary"
        onClick={onSavePlan}
        isDisabled={isSaveDisabled}
        text={`Save ${title} Plan`}
      />
    </span>
  );
  
  const SaveButtonWithTooltip = tooltipText
    ? withTooltip(SaveButtonRenderer, { text: tooltipText, placement: "top" })
    : SaveButtonRenderer;

  return (
    <>
      <Card>
        <div className="create-bundle-card">
          <div className="bundle-pricing-title">
            Pricing - {selectedBundle?.name}
          </div>
          <PricingBreadcrumbs breadcrumbs={breadcrumbs} />

          {fetchingEndpointsAndChannels ? (
            <div>Loading...</div>
          ) : (
            <>
              {selectedSubPlan === "custom" ? (
                <>
                  <div className="bundle_pricing_heading">
                    Custom Plan
                  </div>
                  <div className="input_box">
                    <Input
                      id="sub-plan-input-simple"
                      label="Plan Name"
                      name="sub-plan-input-simple"
                      dataTestId="sub-plan-input-bundle-plan-name-field"
                      size={INPUT_SIZES.LARGE}
                      maxLength={100}
                      helperText="Name of the plan to share with outside Org"
                      isRequired
                      onChange={onChangeTitle}
                      value={title}
                    />
                  </div>
                </>
              ) : (
                <div
                  style={{ marginTop: "17px" }}
                  className="bundle_pricing_heading"
                >
                  {title} Plan
                </div>
              )}
              <div className="add-a-plan-container">
                <div className="input_box">
                  <Input
                    enableCustomValidation
                    id="subscription-price-input"
                    dataTestId="subscription-price-input"
                    label={`${title} Subscription Price`}
                    name="subscription-price-input"
                    size={INPUT_SIZES.LARGE}
                    value={baseValue}
                    onChange={onChangeBaseValue}
                    hasError={!isBaseValueValid}
                    errorMessage="Subscription Price should be 0 or more"
                    helperText="USD per month"
                    prefixText="$"
                    type="number"
                    isRequired
                    onKeyDown={(evt) =>
                      ["e", "E", "+", "-"].includes(evt.key) &&
                      evt.preventDefault()
                    }
                  />
                </div>
                <div className="input_box">
                  <TextArea
                    enableCustomValidation
                    dataTestId="datatest-pricing-plan-description"
                    id="pricing-plan-description"
                    isRequired
                    label="Description"
                    maxLength="100"
                    hasCharacterCount
                    hasError={!isDescriptionValid}
                    errorMessage="Enter description of container"
                    name="pricing-plan-description"
                    onChange={onChangeDescription}
                    helperText="Promotional Text for this plan"
                    value={description}
                  />
                </div>

                <div className="input_box">
                  <div className="advanced-pricing-endpoint-group">
                    <RadioGroup
                      dataTestId="endpoint-group"
                      id="endpoint-group"
                      name="endpoint-group"
                      onChange={onChangeRadio}
                      options={options}
                      value={radioValue}
                    />
                  </div>
                </div>
                {radioValue === "nobucket" ? (
                  <>
                    <div className="input_box">
                      <Input
                        enableCustomValidation
                        label="Quota Limit"
                        id="quota-limit-input"
                        dataTestId="quota-limit-input"
                        value={quotaLimit}
                        onChange={onChangeQuotaLimit}
                        hasError={!isQuotaLimitValid}
                        errorMessage="Quota Limit should be greater than or equal to 0"
                        helperText="Calls per month"
                        type="number"
                        isRequired
                        onKeyDown={(evt) =>
                          ["e", ".", "E", "+", "-"].includes(evt.key) &&
                          evt.preventDefault()
                        }
                      />
                    </div>
                    <div className="input_box">
                      <Input
                        enableCustomValidation
                        label="Overages"
                        id="overages-input"
                        dataTestId="overages-input"
                        value={overages}
                        onChange={onChangeOverages}
                        hasError={!isOveragesValid}
                        errorMessage="Overages should be 0 or more"
                        helperText="USD per extra call"
                        type="number"
                        prefixText="$"
                        isRequired
                        onKeyDown={(evt) =>
                          ["e", "E", "+", "-"].includes(evt.key) &&
                          evt.preventDefault()
                        }
                      />
                    </div>
                  </>
                ) : (
                  <div className="endpoint-container-section">
                    <EndpointContainer
                      pricingPlan={plan}
                      containers={Object.values(subPlanContainers)}
                      setScreen={setScreen}
                      setSelectedSubPlanContainer={setSelectedSubPlanContainer}
                      bundleApiEndpoints={bundleApiEndpoints}
                      bundleApiChannels={bundleApiChannels}
                      onRemoveBucket={onRemoveBucket}
                      onEditContainer={(containerName) => {
                        const { title, baseValue, description } = formValuesRef.current;
                        setDraftSubPlanState({ title, baseValue, description });
                        setSelectedSubPlanContainer(containerName);
                        setScreen(CREATE_A_CONTAINER);
                      }}
                    />
                    <div className="input_box">
                      <SecondaryButton
                        dataTestId="datatest-cancel-plan"
                        iconColor="secondary"
                        onClick={onCreateEndpointContainer}
                        text="Create Endpoint Container"
                      />
                    </div>
                  </div>
                )}
                <div className="sub-plan-buttons">
                  <SaveButtonWithTooltip />
                  <SecondaryButton
                    dataTestId="datatest-cancel-plan"
                    iconColor="secondary"
                    onClick={onCancelPlan}
                    text="Cancel"
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </Card>
    </>
  );
};

export default withNavigationToBundles(AddAPlan);
